import { useEffect, useState } from "react";
import { ActionIcon, Container, Group, Loader, Text } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconEye, IconPencil, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import HttpClient from "../helpers/HttpClient";
import { config } from "../helpers/Config";
import { getAccessToken } from "../helpers/TokenRefresh";
import { Receipt } from "../types/receipt.type";

function ReceiptList() {
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const navigate = useNavigate();

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const result = await new HttpClient<Receipt>("/receipt").getAll();
      setReceipts(result);
    } catch {
      notifications.show({ message: "Fehler beim Laden der Belege", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  const handlePreview = async (receipt: Receipt) => {
    const token = await getAccessToken();
    fetch(`${config.apiUrl}/receipt/${receipt.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, "_blank");
        // Revoke after the new tab has had time to load the blob
        win?.addEventListener("load", () => URL.revokeObjectURL(blobUrl), { once: true });
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch(() => notifications.show({ message: "Vorschau fehlgeschlagen", color: "red" }));
  };

  const handleDelete = async (receipt: Receipt) => {
    try {
      await new HttpClient<Receipt>("/receipt").delete(receipt.id);
      setReceipts(prev => prev.filter(r => r.id !== receipt.id));
      notifications.show({ message: "Beleg gelöscht", color: "green" });
    } catch {
      notifications.show({ message: "Fehler beim Löschen", color: "red" });
    }
  };

  return (
    <Container fluid pb={10} mt={10}>
      <Group gap={25} visibleFrom="xs">
        <h1>Belege</h1>
      </Group>
      {loading ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          borderRadius="sm"
          withColumnBorders
          striped
          highlightOnHover
          records={receipts}
          idAccessor="id"
          columns={[
            {
              accessor: "documentDate",
              title: "Belegdatum",
              width: "12%",
              render: ({ documentDate }) =>
                documentDate ? dayjs(documentDate).format("DD.MM.YYYY") : "—",
            },
            {
              accessor: "createdAt",
              title: "Hochgeladen",
              width: "12%",
              render: ({ createdAt }) =>
                createdAt ? dayjs(createdAt).format("DD.MM.YYYY HH:mm") : "—",
            },
            {
              accessor: "personName",
              title: "Person",
              width: "15%",
              render: ({ personName }) => personName || <Text c="dimmed">—</Text>,
            },
            {
              accessor: "bookingDate",
              title: "Buchungsdatum",
              width: "12%",
              render: ({ bookingDate }) =>
                bookingDate ? dayjs(bookingDate).format("DD.MM.YYYY") : "—",
            },
            {
              accessor: "bookingText",
              title: "Buchungstext",
              render: ({ bookingText }) => bookingText || "—",
            },
            {
              accessor: "fileName",
              title: "Dateiname",
              width: "18%",
            },
            {
              accessor: "actions",
              title: "",
              width: "10%",
              render: (receipt) => (
                <Group gap={4} justify="center">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => handlePreview(receipt)}
                    aria-label="Vorschau"
                  >
                    <IconEye size={18} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="gray"
                    onClick={() => navigate(`/receipt/edit/${receipt.id}`)}
                    aria-label="Bearbeiten"
                  >
                    <IconPencil size={18} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => handleDelete(receipt)}
                    aria-label="Löschen"
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              ),
            },
          ]}
        />
      )}
    </Container>
  );
}

export default ReceiptList;
